import { useQuery } from "@tanstack/react-query";
import { SmartyStreetAddress } from "../types/neighborhoodSearch";
import { Address } from "../types/listing";
import { GeoLocation } from "src/types/common";
import {
  AutoCompleteLookup,
  StreetLookup,
  streetSearchClient,
  autocompleteClient,
  reverseLookupClient,
  ReverseLookup,
} from "src/libs/smartystreetsClient";

export type AddressSuggestion = {
  streetLine: string;
  secondary: string;
  city: string;
  state: string;
  zipcode: string;
  entries: number;
}

// USPS secondary unit designator mappings (singular and plural forms)
const secondaryDesignatorMap: Record<string, { singular: string; plural: string }> = {
  'Apt': { singular: 'Apartment', plural: 'Apartments' },
  'Ste': { singular: 'Suite', plural: 'Suites' },
  'Unit': { singular: 'Unit', plural: 'Units' },
  'Ofc': { singular: 'Office', plural: 'Offices' },
  'Fl': { singular: 'Floor', plural: 'Floors' },
  'Bldg': { singular: 'Building', plural: 'Buildings' },
  'Rm': { singular: 'Room', plural: 'Rooms' },
  'Dept': { singular: 'Department', plural: 'Departments' },
  'Lot': { singular: 'Lot', plural: 'Lots' },
  'Spc': { singular: 'Space', plural: 'Spaces' },
  'Trlr': { singular: 'Trailer', plural: 'Trailers' },
  'Hngr': { singular: 'Hangar', plural: 'Hangars' },
  'Slip': { singular: 'Slip', plural: 'Slips' },
  'Pier': { singular: 'Pier', plural: 'Piers' },
  'Stop': { singular: 'Stop', plural: 'Stops' },
};

export const getSecondaryLabel = (secondary: string, entries: number): string => {
  const mapping = secondaryDesignatorMap[secondary];
  if (mapping) {
    return entries > 1 ? mapping.plural : mapping.singular;
  }
  return secondary;
};

export const searchAddressUsingGeo = async (
  lat: number,
  lng: number
) => {

  try { 
    const lookup = new ReverseLookup(lat.toString(), lng.toString());
    const resp = await reverseLookupClient.send(lookup);
    const firstResult = resp?.response?.results?.[0]?.address;
    return firstResult;
  } catch (error) {
    console.error("Error fetching address details", error);
  }
  return null;
};

export const searchAddress = async ({ street }: { street: string }): Promise<{ address: Address, geoLocation: GeoLocation, smartyStreetAddress: SmartyStreetAddress } | null> => {
  const lookup = new StreetLookup();
  lookup.street = street;
  lookup.match = "enhanced";
  const resp = await streetSearchClient.send(lookup);
  const firstResult = resp?.lookups?.[0]?.result?.[0];

  if (!firstResult) {
    return null
  }

  const { components, metadata } = firstResult;

  return {
    geoLocation: {
      lat: metadata.latitude,
      lng: metadata.longitude,  
    },
    address: {
      city: components.cityName,
      houseNumber: components.primaryNumber,
      postalCode: components.zipCode,
      postalCodePlus4: components.plus4Code,
      state: components.state,
      streetName: components.streetName,
      streetSuffix: components.streetSuffix,
      streetPreDirection: components.streetPredirection,
      streetPostDirection: components.streetPostdirection,
    },
    smartyStreetAddress: components,
  }
};

export const searchAddressesAutoComplete = async (query: string): Promise<AddressSuggestion[]> => {
  const lookup = new AutoCompleteLookup(query);
  (lookup as unknown as { source: string }).source = "all";
  const resp = await autocompleteClient.send(lookup);
  return resp?.result?.map(({
    streetLine,
    secondary,
    city,
    state,
    zipcode,
    entries,
  }) => ({
    streetLine,
    secondary,
    city,
    state,
    zipcode,
    entries: entries ?? 0,
  })) || [];
};

export const useAddressSuggestions = (query: string) =>
  useQuery({
    queryKey: ["addressSuggestions", query],
    queryFn: async () => {
      return searchAddressesAutoComplete(query);
    },
    enabled: !!query,
  });
